import { IAppReducer } from 'models';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getCardsByUserId } from 'shared/services/payment-method';
import { getPaymentMethodLink } from 'shared/services/payment-method-link';
import {
  createLiveBillingPortalSession,
  getLivePaymentMethods,
  getStripeNativeSettingEnabled,
  setLiveDefaultPaymentMethod,
} from 'shared/services/payment-method-live';
import { CreditCard } from '../types/Add-subscription.types';

interface UsePaymentMethodReturn {
  paymentLink: string | null;
  isPaymentLinkModalOpen: boolean;
  isGeneratingLink: boolean;
  generatePaymentLink: () => void;
  handleRefreshCards: () => void;
  isLoading: boolean;
  error: string | null;
  selectedMethod: string | null;
  handleMethodChange: (value: string) => void;
  handleCardSelection: (cardId: string) => void;
  creditCards: CreditCard[];
  showNewCardForm: boolean;
  selectedCard: string | null;
  handleShowNewCardForm: (value: boolean) => void;
  handleIsPaymentLinkModalOpen: (value: boolean) => void;
  handleError: (value: string | null) => void;
  /** CB-18: true cuando esta sesión usa Stripe en vivo (no dLocal) — gatea el selector en vivo, los
   * popups de default y el reemplazo del form por el Billing Portal. */
  isStripeFlow: boolean;
  /** CB-18: tarjeta pendiente de confirmación antes de fijarla como predeterminada (el agente/cliente
   * eligió una tarjeta que no es la default de Stripe entre 2+). */
  pendingDefaultCard: CreditCard | null;
  isConfirmingDefault: boolean;
  handleConfirmSetDefault: () => void;
  handleCancelSetDefault: () => void;
  /** CB-18: true cuando hay 2+ tarjetas y ninguna está marcada como predeterminada en Stripe —
   * bloquea el CTA de compra hasta que se corrija (ver `Add-subscription.tsx`). */
  hasNoDefaultCard: boolean;
  isOpeningPortal: boolean;
  handleAddCardViaPortal: () => void;
  /** CB-18: true solo cuando "Agregar tarjeta"/"Generar link a cliente" deben usar el Billing
   * Portal de Stripe — requiere `isStripeFlow` (procesador Stripe) Y que el feature flag global
   * `stripeNative` esté activo, porque eso es lo mismo que decide el backend (`shouldUseStripeNative`)
   * al crear la suscripción nueva. El resto del flujo (listado en vivo, popups de default, bloqueo
   * del CTA) sigue solo con `isStripeFlow`: eso es correcto sin importar si la suscripción termina
   * siendo legacy o stripe_native, porque en ambos casos se cobra con un PaymentMethod real de
   * Stripe. Ofrecer el portal para una suscripción que va a nacer legacy confundiría al cliente
   * (el portal representa una suscripción de Stripe que no es la que realmente lo factura) — riesgo
   * de churn. */
  willAddCardViaPortal: boolean;
}

interface UsePaymentMethodProps {
  onSelectPaymentMethod: (method: string | null) => void;
}

export const usePaymentMethod = ({
  onSelectPaymentMethod,
}: UsePaymentMethodProps): UsePaymentMethodReturn => {
  const { authReducer: user, branchReducer } = useSelector(
    (state: IAppReducer) => state
  );
  // CB-18: solo el flujo de tarjetas de Stripe (procesador excluido: dLocal) tiene una fuente de
  // verdad en Stripe con la que sincronizar — mismo criterio que `usePaymentMethodForm` ya usa para
  // decidir el form de tarjeta (branch 'pr' o sin formToShow/formToShow='stripe').
  const isStripeFlow =
    branchReducer.branch === 'pr' ||
    !user.formToShow ||
    user.formToShow.toLocaleLowerCase() === 'stripe';

  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [showNewCardForm, setShowNewCardForm] = useState(false);
  const [creditCards, setCreditCards] = useState<CreditCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPaymentLinkModalOpen, setIsPaymentLinkModalOpen] = useState(false);
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [pendingDefaultCardId, setPendingDefaultCardId] =
    useState<string | null>(null);
  const [isConfirmingDefault, setIsConfirmingDefault] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  // CB-18: por defecto false (fail-closed) — mientras no se confirme que el backend va a crear la
  // suscripción como stripe_native, se prefiere el form/link clásico (funciona sin importar el
  // billing_mode resultante).
  const [stripeNativeEnabled, setStripeNativeEnabled] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    getStripeNativeSettingEnabled().then((enabled) => {
      if (isMountedRef.current) setStripeNativeEnabled(enabled);
    });
  }, []);

  const willAddCardViaPortal = isStripeFlow && stripeNativeEnabled;

  const fetchCards = async (): Promise<CreditCard[]> =>
    isStripeFlow
      ? (await getLivePaymentMethods(user)).cards
      : await getCardsByUserId(user.uid);

  // CB-18: `handleConfirmSetDefault` y `handleRefreshCards` pueden solaparse (ej. el agente confirma
  // un cambio de default y casi al mismo tiempo usa el botón de recargar) — sin esto, la respuesta
  // que tarde más en resolver podría pisar a una más reciente y dejar visualmente la tarjeta
  // predeterminada anterior. Cada llamada se marca con un id incremental; solo se aplica
  // `setCreditCards` si sigue siendo la más reciente al resolver.
  const cardsRequestIdRef = useRef(0);
  const fetchAndApplyCards = async (): Promise<CreditCard[]> => {
    const requestId = ++cardsRequestIdRef.current;
    const cards = await fetchCards();
    if (isMountedRef.current && cardsRequestIdRef.current === requestId) {
      setCreditCards(cards);
    }
    return cards;
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const cards = await fetchCards();
        if (!cancelled) setCreditCards(cards);
      } catch (err) {
        if (!cancelled) {
          console.log(err);
          setError(
            'Error al cargar las tarjetas. Por favor, intente de nuevo.'
          );
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.uid, isStripeFlow]);

  // CB-18: bug real detectado probando add-subscription — al agregar una tarjeta por el form
  // clásico, `handleRefreshCards` sí trae la lista actualizada (por eso el badge "PRINCIPAL" se ve
  // bien en la tarjeta correcta), pero `selectedCard` nunca se revalidaba contra esos datos nuevos:
  // la tarjeta que quedaba visualmente seleccionada podía dejar de ser la default real de Stripe sin
  // que nadie lo notara. Mismo patrón de bug (y mismo fix) que ya se corrigió en
  // payment-method-selector.tsx (nextjs-soda-client-app, edit-subscription): re-sincroniza la
  // selección con la tarjeta principal real cada vez que `creditCards` cambia. `lastProcessedCardsRef`
  // distingue un refresh real (nueva referencia de array) de un re-render sin datos nuevos. Nunca
  // fuerza la selección a `null` si ya había algo elegido y ahora ninguna es principal — eso lo
  // cubre `hasNoDefaultCard` bloqueando el CTA, no borrando la selección visible.
  const lastProcessedCardsRef = useRef<CreditCard[] | null>(null);
  useEffect(() => {
    if (selectedMethod !== 'credit_card') return;
    const isNewCardsData = lastProcessedCardsRef.current !== creditCards;
    lastProcessedCardsRef.current = creditCards;
    if (!isNewCardsData) return;

    const principalCard = creditCards.find((card) => card.is_principal);
    const currentSelectionStillPrincipal =
      selectedCard != null && principalCard?.id === selectedCard;
    if (currentSelectionStillPrincipal) return;

    if (principalCard) {
      setSelectedCard(principalCard.id);
      onSelectPaymentMethod(principalCard.id);
    } else if (!selectedCard && creditCards.length > 0) {
      setSelectedCard(creditCards[0].id);
      onSelectPaymentMethod(creditCards[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditCards, selectedMethod]);

  const handleShowNewCardForm = (value: boolean) => {
    setShowNewCardForm(value);
  };

  const handleIsPaymentLinkModalOpen = (value: boolean) => {
    setIsPaymentLinkModalOpen(value);
  };

  const handleError = (value: string | null) => {
    setError(value);
  };

  const handleMethodChange = (value: string) => {
    setSelectedMethod(value);
    if (value === 'credit_card') {
      const principalCard = creditCards.find((card) => card.is_principal);
      if (principalCard) {
        setSelectedCard(principalCard.id);
        onSelectPaymentMethod(principalCard.id);
      } else {
        setSelectedCard(null);
        onSelectPaymentMethod(null);
      }
    } else {
      setSelectedCard(null);
      onSelectPaymentMethod(null);
    }
    setShowNewCardForm(false);
  };

  const handleCardSelection = (cardId: string) => {
    // CB-18: elegir una tarjeta que no es la default de Stripe entre 2+ pasará a serlo — se pide
    // confirmación explícita antes de fijarla (`handleConfirmSetDefault`) en vez de seleccionarla
    // de una. Con 1 sola tarjeta no aplica (regla 1: fuente de verdad incondicional).
    const card = creditCards.find((c) => c.id === cardId);
    if (isStripeFlow && creditCards.length > 1 && card && !card.is_principal) {
      setPendingDefaultCardId(cardId);
      return;
    }
    setSelectedCard(cardId);
    onSelectPaymentMethod(cardId);
    setShowNewCardForm(false);
  };

  const pendingDefaultCard =
    creditCards.find((c) => c.id === pendingDefaultCardId) || null;

  const handleCancelSetDefault = () => {
    setPendingDefaultCardId(null);
  };

  const handleConfirmSetDefault = async () => {
    if (!pendingDefaultCardId) return;
    setIsConfirmingDefault(true);
    try {
      await setLiveDefaultPaymentMethod(user, pendingDefaultCardId);
      await fetchAndApplyCards();
      if (!isMountedRef.current) return;
      setSelectedCard(pendingDefaultCardId);
      onSelectPaymentMethod(pendingDefaultCardId);
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error(err);
      setError(
        'Error al establecer la tarjeta predeterminada. Intenta de nuevo.'
      );
    } finally {
      if (isMountedRef.current) {
        setIsConfirmingDefault(false);
        setPendingDefaultCardId(null);
      }
    }
  };

  // CB-18: bloquea el CTA de compra (ver `Add-subscription.tsx`) con 2+ tarjetas cuando no hay una
  // elección segura — ya sea porque ninguna está marcada default en Stripe, o porque la tarjeta
  // seleccionada dejó de ser la default (el efecto de arriba corrige esto casi siempre; esto es la
  // red de seguridad para la ventana breve entre que cambian los datos y el efecto corre). Se
  // corrige en Stripe (ej. vía el Billing Portal que esta misma vista ya ofrece) y no seleccionando
  // cualquier tarjeta a ciegas.
  const selectedCardObj = creditCards.find((card) => card.id === selectedCard);
  const hasNoDefaultCard =
    isStripeFlow &&
    selectedMethod === 'credit_card' &&
    creditCards.length > 1 &&
    (!selectedCardObj || !selectedCardObj.is_principal);

  const generatePaymentLink = async () => {
    setIsGeneratingLink(true);
    try {
      if (willAddCardViaPortal) {
        const { url } = await createLiveBillingPortalSession(user);
        if (!isMountedRef.current) return;
        setPaymentLink(url);
        setIsPaymentLinkModalOpen(true);
      } else {
        const paymentMethodLink = await getPaymentMethodLink(user.uid);
        if (!isMountedRef.current) return;
        setPaymentLink(paymentMethodLink.url);
        setIsPaymentLinkModalOpen(true);
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      setError('Error al generar el enlace de pago');
    } finally {
      if (isMountedRef.current) setIsGeneratingLink(false);
    }
  };

  const handleAddCardViaPortal = async () => {
    // CB-18: `window.open` después de un `await` puede quedar bloqueado por el navegador (solo se
    // permite sin restricciones cuando ocurre de forma síncrona dentro del gesto del usuario) — se
    // pre-abre una pestaña en blanco ACÁ, antes de cualquier `await`, y se navega esa misma pestaña
    // a la URL real una vez que la sesión del portal está lista. No se puede pasar `noopener`/
    // `noreferrer` en este `open` porque eso hace que el navegador devuelva `null` en vez de la
    // referencia que necesitamos para navegarla después.
    const portalWindow = window.open('', '_blank');
    if (!portalWindow) {
      setError(
        'El navegador bloqueó la ventana emergente. Habilita las ventanas emergentes para este sitio e intenta de nuevo.'
      );
      return;
    }
    setIsOpeningPortal(true);
    try {
      const { url } = await createLiveBillingPortalSession(user);
      if (!isMountedRef.current) {
        portalWindow.close();
        return;
      }
      portalWindow.location.href = url;
    } catch (err) {
      portalWindow.close();
      if (!isMountedRef.current) return;
      console.error(err);
      setError('Error al abrir el portal de pago de Stripe');
    } finally {
      if (isMountedRef.current) setIsOpeningPortal(false);
    }
  };

  const handleRefreshCards = async () => {
    handleShowNewCardForm(false);
    setIsLoading(true);
    setError(null);
    try {
      await fetchAndApplyCards();
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error(err);
      setError(
        'Error al actualizar las tarjetas. Por favor, intente de nuevo.'
      );
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  };

  return {
    paymentLink,
    isPaymentLinkModalOpen,
    isGeneratingLink,
    generatePaymentLink,
    handleRefreshCards,
    isLoading,
    error,
    selectedMethod,
    handleMethodChange,
    handleCardSelection,
    creditCards,
    showNewCardForm,
    selectedCard,
    handleShowNewCardForm,
    handleIsPaymentLinkModalOpen,
    handleError,
    isStripeFlow,
    pendingDefaultCard,
    isConfirmingDefault,
    handleConfirmSetDefault,
    handleCancelSetDefault,
    hasNoDefaultCard,
    isOpeningPortal,
    handleAddCardViaPortal,
    willAddCardViaPortal,
  };
};
